/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useRef, useState } from "react";
import { useSetRecoilState } from "recoil";
import { TempCampaignAtom } from "../../../../atoms/CampaignAtom";
import useClickOutside from "../../../../hook/useClickOutside";
import { CampaignValidation } from "../../../../pages/create-campaign";
import { INode, INodeTypes } from "../../../../types/CampaignTypes";
import IconBerserkerMail from "../../../Icon";
import MergeFields from "../../../NewEmail/MergeFields";
import StyledAutocompleteSelect from "../../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../../StyledButton";
import StyledInput from "../../../StyledInput";
import Delay from "./Delay";
import Form, { DefaultFormProps, FormInstance } from "./Form";

const Container = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

export const Title = styled.div`
  color: #2f2f2f;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0px;
  margin-bottom: 18px;
  display: flex;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

export const Column = styled.div`
  display: flex;
  flex-direction: row;
`;

export const Label = styled.span`
  color: #2f2f2f;
  letter-spacing: 0px;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
`;

interface NamePairButton {
  margin: boolean;
}

const NamePairButton = styled(StyledButton)<NamePairButton>`
  display: flex;
  flex-direction: row;
  width: 40px;
  height: 40px;
  border: 1px solid #dddddd;
  border-radius: 5px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: white;
  margin-right: ${({ margin }) => (margin ? "15px" : "0")};
  :focus {
    box-shadow: 0px 0px 6px #fb972e4d;
    border: 1px solid #fb972e;
  }
`;

const Optional = styled.span`
  font-size: 16px;
  font-weight: normal;
  letter-spacing: 0px;
  color: #2f2f2f;
  opacity: 0.5;
`;

const HttpToggleButton = styled.span`
  font: normal normal 600 15px;
  position: absolute;
  cursor: pointer;
  left: 5px;
  top: 5px;
  align-items: center;
  justify-content: center;
  width: 70px;
  font-weight: 600;
  height: 30px;
  display: flex;
  background-color: #fff2e5;
  border-radius: 4px;
  color: #fb972e;
  border: 1px solid #fb972e;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
`;

const AUTH_TYPE = [
  {
    value: "bearer",
    name: "Bearer",
  },
  {
    value: "basic",
    name: "Basic",
  },
];

const CONTENT_TYPE: { value: "form-data" | "json"; name: string }[] = [
  {
    value: "form-data",
    name: "Form-Data",
  },
  {
    value: "json",
    name: "JSON",
  },
];

const validateFields = (nameValuePairs) => {
  const names: string[] = [];
  let response = true;

  for (let i = 0; i < nameValuePairs.length; i = i + 1) {
    if (!nameValuePairs[0].name.trim() && nameValuePairs.length > 1) {
      response = false;
      break;
    }
    if (!nameValuePairs[i].name.trim() && i > 0) {
      response = false;
      break;
    }
    if (names.includes(nameValuePairs[i].name)) {
      response = false;
      break;
    }
    names.push(nameValuePairs[i].name.trim());
  }
  return response;
};

export function validateWebhook(node: INode): CampaignValidation {
  if (node.type === INodeTypes.WebHook && node.webHook) {
    const {
      name,
      webHook: { url, fields, authorization, contentType },
    } = node;

    const normalizedFields = fields || [];

    if (url.trim() && name.trim() && validateFields(normalizedFields)) {
      if (!url.includes("http://") && !url.includes("https://")) {
        return {
          node: null,
          error: {
            toastInfo: {
              toastText:
                "Webhook URL must contain 'http://' or 'https:// prefix.'",
              toastType: "info",
            },
          },
        };
      }

      let webhookFields = [...normalizedFields];
      if (
        webhookFields.length &&
        !webhookFields[0].name.trim() &&
        webhookFields.length === 1
      ) {
        webhookFields = [];
      }

      const newAction: INode = {
        _id: node?._id,
        name: name.trim(),
        type: INodeTypes.WebHook,
        webHook: {
          url: url.trim(),
          contentType,
          fields: webhookFields.map((field) => ({
            ...field,
            name: field.name.trim(),
          })),
          authorization,
        },
      };

      return { node: newAction, error: null };
    } else {
      return {
        node: null,
        error: {
          toastInfo: {
            toastText: "Please, fill all the required fields",
            toastType: "info",
          },
        },
      };
    }
  } else {
    return {
      node: null,
      error: {
        toastInfo: {
          toastText: "Please, fill all the required fields",
          toastType: "info",
        },
      },
    };
  }
}

interface NamePairControlProps {
  icon: string;
  onClick: () => any;
  margin?: boolean;
  disabled: boolean;
}

const NamePairControl = ({
  icon,
  onClick,
  margin,
  disabled,
}: NamePairControlProps) => (
  <NamePairButton onClick={onClick} disable={disabled} margin={margin}>
    <IconBerserkerMail name={icon} />
  </NamePairButton>
);

interface NamePairItem {
  nameValuePairs: { name: string; value: string }[];
  setNameValuePairs: (value: { name: string; value: string }[]) => any;
  focusedIndex: number;
  setFocusedIndex: (value: number) => any;
  putFocus: (value: "next" | "previous", index: number) => any;
  index: number;
  disabled: boolean;
}

const NamePairItem = ({
  index,
  nameValuePairs,
  setNameValuePairs,
  focusedIndex,
  setFocusedIndex,
  putFocus,
  disabled,
}: NamePairItem) => {
  const [showMergeMenu, setShowMergeMenu] = useState(false);
  const keyInputRef = useRef<HTMLInputElement | null>(null);
  const valueInputRef = useRef<HTMLInputElement | null>(null);
  const [cursorLocation, setCursorLocation] = useState<
    "KEY" | "VALUE" | "NONE"
  >("NONE");

  useEffect(() => {
    if (valueInputRef.current && keyInputRef.current) {
      keyInputRef.current.addEventListener("focus", () => {
        setCursorLocation("KEY");
      });
      valueInputRef.current.addEventListener("focus", () => {
        setCursorLocation("VALUE");
      });
    }

    return () => {
      setCursorLocation("NONE");
    };
  }, []);

  useEffect(() => {
    if (focusedIndex === -1) {
      setCursorLocation("NONE");
    }
  }, [focusedIndex]);

  function insertMergedField(value: string, field: "name" | "value") {
    let newValuePairs = [...nameValuePairs];
    newValuePairs[index] = {
      ...newValuePairs[index],
      [field]: value,
    };
    setNameValuePairs(newValuePairs);
  }

  function handleFieldSelected(field: string) {
    if (cursorLocation === "NONE") return;

    const inputRef = cursorLocation === "VALUE" ? valueInputRef : keyInputRef;

    const start = inputRef.current.selectionStart;
    const end = inputRef.current.selectionEnd;
    const text = inputRef.current.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);

    insertMergedField(
      `${before}${field}${after}`,
      cursorLocation === "VALUE" ? "value" : "name"
    );

    inputRef.current.selectionStart = inputRef.current.selectionEnd =
      start + field.length;
    inputRef.current.focus();
  }

  return (
    <Column
      css={
        index > 0 &&
        css`
          margin-top: 10px;
        `
      }
      onFocus={() => setFocusedIndex(index)}
    >
      <StyledInput
        ref={keyInputRef}
        placeholder="Enter name"
        css={css`
          margin-right: 20px;
          flex: 1;
        `}
        value={nameValuePairs[index].name}
        onChange={(e: any) => {
          let newValuePairs = [...nameValuePairs];
          newValuePairs[index] = {
            ...newValuePairs[index],
            name: e.target.value,
          };
          setNameValuePairs(newValuePairs);
        }}
        disabled={disabled}
      />
      <StyledInput
        ref={valueInputRef}
        placeholder="Enter value"
        css={css`
          margin-right: 20px;
          flex: 1;
          margin-right: ${index !== focusedIndex && "218px"};
        `}
        value={nameValuePairs[index].value}
        onChange={(e: any) => {
          let newValuePairs = [...nameValuePairs];
          newValuePairs[index] = {
            ...newValuePairs[index],
            value: e.target.value,
          };
          setNameValuePairs(newValuePairs);
        }}
        disabled={disabled}
      />
      {index === focusedIndex && (
        <>
          <MergeFields
            isSetting
            selectedVariable={(value) => handleFieldSelected(value)}
            removeVirtualFields
          >
            <div>
              <StyledButton
                inverted
                css={css`
                  height: 40px;
                  margin-right: 15px;
                  :hover {
                    opacity: 1 !important;
                  }
                `}
              >
                Merge
              </StyledButton>
            </div>
          </MergeFields>

          <NamePairControl
            icon={"add-pair"}
            onClick={() => {
              const nextIndex =
                index > nameValuePairs.length - 1
                  ? nameValuePairs.length - 1
                  : index + 1;
              setNameValuePairs([...nameValuePairs, { name: "", value: "" }]);
              setFocusedIndex(nextIndex);
              putFocus("next", nextIndex);
            }}
            margin
            disabled={disabled}
          />
          <NamePairControl
            icon={"remove-pair"}
            onClick={() => {
              const previousIndex =
                index >= nameValuePairs.length - 1 ? index - 1 : index;
              if (nameValuePairs.length !== 1) {
                setNameValuePairs(
                  nameValuePairs.filter((item, i) => index !== i)
                );
                setFocusedIndex(previousIndex);
                putFocus("previous", previousIndex);
              } else {
                setNameValuePairs([{ name: "", value: "" }]);
              }
            }}
            disabled={disabled}
          />
        </>
      )}
    </Column>
  );
};

const Webhook = ({ action, nodeIndex }: DefaultFormProps): JSX.Element => (
  <Form {...{ action, nodeIndex }}>
    {({ updateCampaign, tempCampaign }: FormInstance) => {
      const setTempCampaign = useSetRecoilState(TempCampaignAtom);
      const [showMergeMenu, setShowMergeMenu] = useState(false);
      const [isHttps, setIsHttps] = useState(true);
      const [focusedIndex, setFocusedIndex] = useState<number>(-1);
      const textAreaUrlRef = useRef<HTMLInputElement | null>(null);
      const ref = useClickOutside(() => {
        setFocusedIndex(-1);
      });

      const currentNode = tempCampaign?.nodes[nodeIndex];

      const name = currentNode?.name || "";
      const setName = (name) =>
        setTempCampaign({
          ...tempCampaign,
          nodes: [
            ...tempCampaign.nodes.slice(0, nodeIndex),
            {
              ...currentNode,
              name,
            },
            ...tempCampaign.nodes.slice(nodeIndex + 1),
          ],
        });

      const url = currentNode?.webHook?.url || "";
      const setUrl = (url) =>
        setTempCampaign({
          ...tempCampaign,
          nodes: [
            ...tempCampaign.nodes.slice(0, nodeIndex),
            {
              ...currentNode,
              webHook: {
                ...currentNode.webHook,
                url,
              },
            },
            ...tempCampaign.nodes.slice(nodeIndex + 1),
          ],
        });

      const nameValuePairs = currentNode?.webHook?.fields || [
        { name: "", value: "" },
      ];
      const setNameValuePairs = (fields) =>
        setTempCampaign({
          ...tempCampaign,
          nodes: [
            ...tempCampaign.nodes.slice(0, nodeIndex),
            {
              ...currentNode,
              webHook: {
                ...currentNode.webHook,
                fields,
              },
            },
            ...tempCampaign.nodes.slice(nodeIndex + 1),
          ],
        });

      const contentType =
        CONTENT_TYPE.find(
          (ct) => currentNode?.webHook?.contentType === ct.value
        ) || CONTENT_TYPE[0];
      const setContentType = (contentType) =>
        setTempCampaign({
          ...tempCampaign,
          nodes: [
            ...tempCampaign.nodes.slice(0, nodeIndex),
            {
              ...currentNode,
              webHook: {
                ...currentNode.webHook,
                contentType: contentType.value,
              },
            },
            ...tempCampaign.nodes.slice(nodeIndex + 1),
          ],
        });

      const setAuthorization = ({
        authorization,
        authorizationType,
        authorizationPass,
        authorizationUser,
      }) => {
        let authToken =
          authorizationType.value === "basic"
            ? btoa((authorizationUser || "") + ":" + (authorizationPass || ""))
            : authorization;

        const newAuthorization = `${
          authorizationType.value === "basic" ? "Basic " : "Bearer "
        }${authToken}`;

        setTempCampaign({
          ...tempCampaign,
          nodes: [
            ...tempCampaign.nodes.slice(0, nodeIndex),
            {
              ...currentNode,
              webHook: {
                ...currentNode.webHook,
                authorization: newAuthorization,
              },
            },
            ...tempCampaign.nodes.slice(nodeIndex + 1),
          ],
        });
      };

      useEffect(() => {
        if (url.toLowerCase().indexOf("http://") === 0) {
          setIsHttps(false);
        } else if (url.toLowerCase().indexOf("https://") === 0) {
          setIsHttps(true);
        }
      }, [url]);

      function printUnprefixedURL() {
        return url.replace(/^https?:\/\//gi, "");
      }

      const {
        authorization,
        authorizationType,
        authorizationPass,
        authorizationUser,
      } = (() => {
        const nodeAuthorization = currentNode?.webHook?.authorization || "";

        if (nodeAuthorization.includes("Bearer")) {
          return {
            authorization: nodeAuthorization.replace("Bearer ", ""),
            authorizationType: AUTH_TYPE[0],
            authorizationPass: "",
            authorizationUser: "",
          };
        } else {
          let basicToken = nodeAuthorization.replace("Basic ", "");
          basicToken = atob(basicToken);
          const [user, pass] = basicToken.split(":");

          return {
            authorization: nodeAuthorization.replace("Basic ", ""),
            authorizationType: AUTH_TYPE[1],
            authorizationPass: pass || "",
            authorizationUser: user || "",
          };
        }
      })();

      const saveForm = () => {};

      function handleFieldSelected(field: string) {
        if (!textAreaUrlRef.current) return;
        const start = textAreaUrlRef.current.selectionStart;
        const end = textAreaUrlRef.current.selectionEnd;
        const text = textAreaUrlRef.current.value;
        const before = text.substring(0, start);
        const after = text.substring(end, text.length);

        setUrl(
          (isHttps ? "https://" : "http://") + `${before}${field}${after}`
        );

        textAreaUrlRef.current.selectionStart =
          textAreaUrlRef.current.selectionEnd = start + field.length;
        textAreaUrlRef.current.focus();
      }

      const putFocus = (value: "next" | "previous", index: number) => {
        if (!ref.current) return;
        if (value === "next") {
          const element = ref.current.querySelector(
            `div > div > div:nth-child(${index + 2}) > div > input`
          ) as any;
          if (element) element.focus();
        } else {
          const element = ref.current.querySelector(
            `div > div > div:nth-child(${index + 2}) > div > input`
          ) as any;
          if (element) element.focus();
        }
      };

      const Component = (
        <Container>
          <Delay {...{ action, nodeIndex, tempCampaign }} />
          <Row
            css={css`
              padding: 30px;
              padding-bottom: 0;
            `}
          >
            <Title>
              <IconBerserkerMail name="webhook" />
              &nbsp;WEBHOOK
            </Title>
            <Row>
              <Label>Webhook name</Label>
              <StyledInput
                placeholder="Enter webhook name"
                value={name}
                onChange={(evt) => setName(evt.target.value)}
              />
            </Row>
            <Row>
              <Label>URL</Label>
              <Column>
                <div
                  css={css`
                    position: relative;
                    margin-right: 20px;
                    height: 100%;
                    width: 100%;
                  `}
                >
                  <StyledInput
                    ref={textAreaUrlRef}
                    placeholder="Enter URL"
                    css={css`
                      border-radius: 0.375rem !important;
                      text-indent: 75px;
                    `}
                    value={printUnprefixedURL()}
                    onChange={(e) => {
                      let targetValue = e.target.value;

                      let https = isHttps;

                      if (targetValue.indexOf("https://") === 0) {
                        https = true;
                        targetValue = targetValue.replace(/^https?:\/\//gi, "");
                      } else if (targetValue.indexOf("http://") === 0) {
                        https = false;
                        targetValue = targetValue.replace(/^https?:\/\//gi, "");
                      }

                      targetValue =
                        (https ? "https://" : "http://") + targetValue;
                      setUrl(targetValue);
                    }}
                  />
                  <HttpToggleButton
                    onClick={() => {
                      setUrl(
                        url.replace(
                          isHttps ? "https://" : "http://",
                          isHttps ? "http://" : "https://"
                        )
                      );
                    }}
                  >
                    {isHttps ? "https://" : "http://"}
                  </HttpToggleButton>
                </div>

                <MergeFields
                  isSetting
                  selectedVariable={(value) => handleFieldSelected(value)}
                  removeVirtualFields
                >
                  <div>
                    <StyledButton
                      inverted
                      css={css`
                        height: 40px;
                        :hover {
                          opacity: 1 !important;
                        }
                      `}
                    >
                      Merge
                    </StyledButton>
                  </div>
                </MergeFields>
              </Column>
            </Row>
            <Row>
              <Label>
                Authorization <Optional>(Optional)</Optional>
              </Label>
              <Column>
                <div
                  css={css`
                    width: 200px;
                  `}
                >
                  <StyledAutocompleteSelect
                    options={AUTH_TYPE}
                    selectedOption={authorizationType}
                    onOptionSelect={(value) =>
                      setAuthorization({
                        authorization,
                        authorizationType: value,
                        authorizationPass,
                        authorizationUser,
                      })
                    }
                    placeholder={"Select an option"}
                    noTimer
                    disabled={false}
                  />
                </div>
                {authorizationType.value === "bearer" ? (
                  <StyledInput
                    css={css`
                      margin-left: 10px;
                      display: flex;
                      flex: 1;
                    `}
                    placeholder="Enter authorization token"
                    value={authorization}
                    onChange={(evt) =>
                      setAuthorization({
                        authorization: evt.target.value,
                        authorizationType,
                        authorizationPass,
                        authorizationUser,
                      })
                    }
                  />
                ) : (
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                      flex: 1;
                    `}
                  >
                    <StyledInput
                      css={css`
                        margin-left: 10px;
                        flex: 1;
                      `}
                      placeholder="Enter the API username"
                      value={authorizationUser}
                      onChange={(evt) =>
                        setAuthorization({
                          authorization,
                          authorizationType,
                          authorizationPass,
                          authorizationUser: evt.target.value,
                        })
                      }
                    />
                    <StyledInput
                      css={css`
                        margin-left: 10px;
                        flex: 1;
                      `}
                      placeholder="Enter the API password"
                      value={authorizationPass}
                      onChange={(evt) =>
                        setAuthorization({
                          authorization,
                          authorizationType,
                          authorizationPass: evt.target.value,
                          authorizationUser,
                        })
                      }
                    />
                  </div>
                )}
              </Column>
            </Row>

            <Row>
              <Label>Content Type</Label>
              <Column>
                <StyledAutocompleteSelect
                  options={CONTENT_TYPE}
                  selectedOption={contentType}
                  onOptionSelect={(value) => {
                    setContentType(value as any);
                  }}
                  disabled={false}
                  placeholder={"Select an option"}
                  noTimer
                />
              </Column>
            </Row>

            <Row ref={ref}>
              <Label>Name/Value Pairs</Label>
              {nameValuePairs.map((pair, index) => (
                <div>
                  <NamePairItem
                    {...{
                      index,
                      nameValuePairs,
                      setNameValuePairs,
                      focusedIndex,
                      setFocusedIndex,
                      putFocus,
                      disabled: false,
                    }}
                  />
                </div>
              ))}
            </Row>
          </Row>
        </Container>
      );

      return {
        Component,
        saveForm,
      };
    }}
  </Form>
);

export default Webhook;
